// Loan Compliance Module v1.0 PR 3 — open ComplianceExceptions for this
// loan filtered to Track 1 Operations. Resolve action is gated by
// Compliance.ResolveExceptions and POSTs to the PR 1 resolve endpoint.

import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  useComplianceExceptions,
  useResolveComplianceException,
} from "@/hooks/compliance/useCompliance";
import { calendarDate } from "../common/colorBand";

interface OpenExceptionsPanelProps {
  loanId: string;
  canResolve: boolean;
}

const OPEN_STATUSES = new Set(["Open", "InRemediation"]);

function severityBadge(sev: string) {
  if (sev === "Critical") return "error" as const;
  if (sev === "Material") return "review" as const;
  if (sev === "Process") return "info" as const;
  return "archived" as const;
}

export function OpenExceptionsPanel({
  loanId,
  canResolve,
}: OpenExceptionsPanelProps) {
  const toast = useToast();
  const { data, isLoading } = useComplianceExceptions({
    entityId: loanId,
    scope: "Loan",
    track: "Track1Operations",
  });
  const resolve = useResolveComplianceException(loanId);

  const open = useMemo(
    () => (data ?? []).filter((x) => OPEN_STATUSES.has(x.status)),
    [data],
  );

  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const doResolve = async (id: string) => {
    setResolvingId(id);
    try {
      await resolve.mutateAsync({ id, body: { note: null } });
      toast.success("Exception resolved.");
    } catch (err) {
      const msg =
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not resolve exception.";
      toast.error(msg);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Card>
      <div className="mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Open exceptions</h3>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          Track 1 Operations exceptions still open on this loan.
        </p>
      </div>

      {isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : open.length === 0 ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="legal-ops-open-exceptions-empty"
        >
          No open Legal Ops exceptions.
        </p>
      ) : (
        <Table data-testid="legal-ops-open-exceptions-table">
          <THead>
            <TR>
              <TH>Severity</TH>
              <TH>Module</TH>
              <TH>Title</TH>
              <TH>Cure target</TH>
              <TH>Status</TH>
              <TH aria-label="actions" />
            </TR>
          </THead>
          <TBody>
            {open.map((e) => (
              <TR
                key={e.id}
                data-testid={`legal-ops-open-exception-${e.id}`}
              >
                <TD>
                  <Badge status={severityBadge(e.severity)}>{e.severity}</Badge>
                </TD>
                <TD className="font-mono text-caption">{e.sourceModuleCode}</TD>
                <TD>{e.title}</TD>
                <TD>{calendarDate(e.cureTargetDate)}</TD>
                <TD>{e.status}</TD>
                <TD>
                  {canResolve && (
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => void doResolve(e.id)}
                      disabled={resolvingId === e.id}
                      data-testid={`legal-ops-resolve-${e.id}`}
                    >
                      Resolve
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
